import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import DateBox from "./DateBox";

function Calendar ({ events = [] }) {

  const [currentDate, setCurrentDate] = useState(dayjs());
  const today = dayjs();

  // Navigation handlers
  const prevMonth = () => setCurrentDate((d) => d.subtract(1, "month"));
  const nextMonth = () => setCurrentDate((d) => d.add(1, "month"));

  // Generate month grid: fill to full weeks (35 or 42 cells)
  const generateMonthGrid = () => {
    const startOfMonth = currentDate.startOf("month");
    const endOfMonth = currentDate.endOf("month");
    const startDayOfWeek = startOfMonth.day();
    const daysInMonth = endOfMonth.date();

    const daysArray = [];

    // Leading days from previous month
    for (let i = 0; i < startDayOfWeek; i++) {
      daysArray.push({
        date: startOfMonth.subtract(startDayOfWeek - i, "day"),
        inCurrentMonth: false
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      daysArray.push({ date: startOfMonth.date(i), inCurrentMonth: true });
    }

    // Tail days to complete the grid
    const total = daysArray.length;
    const gridCells = Math.ceil(total / 7) * 7; // 35 or 42 typically
    const remainingCells = gridCells - total;
    for (let i = 1; i <= remainingCells; i++) {
      daysArray.push({ date: endOfMonth.add(i, "day"), inCurrentMonth: false });
    }

    return daysArray;
  };

  const daysInGrid = generateMonthGrid();

  const groupedEvents = useMemo(() => {
    const grouped = {};
    for (const event of events) {
      const dateKey = dayjs(event.startDatetime).format("YYYY-MM-DD");
      if (!grouped[dateKey]) grouped[dateKey] = [];
      grouped[dateKey].push(event);
    }
    return grouped;
  }, [events]);

  return (
    <section className="party-calendar" aria-label="Party calendar">
      <header className="party-calendar-header">
        <div aria-live="polite" aria-atomic="true">
          <p className="party-calendar-eyebrow">Find your next night out</p>
          <h2>{currentDate.format("MMMM")} <span>{currentDate.format("YYYY")}</span></h2>
        </div>
        <div className="party-calendar-navigation">
          <button type="button" onClick={prevMonth} aria-label="Previous month"><FiChevronLeft aria-hidden="true" /></button>
          <button type="button" onClick={nextMonth} aria-label="Next month"><FiChevronRight aria-hidden="true" /></button>
        </div>
      </header>
      <div className="party-calendar-grid">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
          <span className="party-calendar-weekday" key={day}>{day}</span>
        ))}
        {daysInGrid.map(dayObj => (
          <DateBox key={dayObj.date.format("YYYY-MM-DD")} dayObj={dayObj} today={today}
            events={groupedEvents[dayObj.date.format("YYYY-MM-DD")] || []} />
        ))}
      </div>
      <footer className="party-calendar-legend">
        <span><i className="calendar-legend-event" aria-hidden="true" /> Events</span>
        <span><i className="calendar-legend-today" aria-hidden="true" /> Today</span>
        <span className="party-calendar-hint">Select a date to explore</span>
      </footer>
    </section>
  );
}

export default Calendar;
